import React from "react";
import { PreloaderGif } from "./Preloader";
import { AutoSaveStatus } from "../providers/AudioProvider/types";

interface SaveButtonProps {
  context: {
    state: {
      savingStatus: boolean;
      autoSaveStatus: AutoSaveStatus;
    };
    handles: {
      flushDirtyChanges: () => Promise<void>;
    };
  };
}

/** «Сохранить сейчас» не успело или сервер не принял правки — дальше (в эфир) идти нельзя. */
export class SaveNowError extends Error {
  constructor(public reason: "timeout" | "refused", message: string) {
    super(message);
  }
}

export interface SaveNowDeps {
  /** То, что делает «Click to save now»: flushGate (ждёт идущее сохранение) или flushDirtyChanges. */
  flush: () => Promise<void>;
  /** Свежее состояние кнопки — из ref, а не из замыкания. */
  read: () => { savingStatus: boolean; autoSaveStatus: AutoSaveStatus };
  timeoutMs?: number;
  pollMs?: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Сохранить сейчас и дождаться «Saved» (не дольше timeoutMs): как нажатие «Click to save now», только с
 * обещанием. Отказ сервера → SaveNowError("refused"), не дождались → SaveNowError("timeout").
 * Пока ждём, человек мог поправить ещё — новые правки тоже уходят.
 */
export async function saveNow({ flush, read, timeoutMs = 30000, pollMs = 200 }: SaveNowDeps): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  const runFlush = async () => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new SaveNowError("timeout", "не сохранилось за 30 с")),
        Math.max(0, deadline - Date.now()),
      );
    });
    try {
      await Promise.race([flush(), late]);
    } catch (err) {
      if (err instanceof SaveNowError) throw err;
      throw new SaveNowError("refused", (err as Error)?.message || String(err));
    } finally {
      if (timer) clearTimeout(timer);
    }
  };
  await runFlush();
  for (;;) {
    const { savingStatus, autoSaveStatus } = read();
    if (!savingStatus && (autoSaveStatus === "idle" || autoSaveStatus === "saved")) return;
    if (Date.now() >= deadline) throw new SaveNowError("timeout", "не сохранилось за 30 с");
    // «Unsaved…» — правка пришла, пока ждали; «Save error» — сервер не принял: ещё раз, как кнопкой.
    if (!savingStatus && (autoSaveStatus === "pending" || autoSaveStatus === "error")) {
      await runFlush();
    }
    await sleep(pollMs);
  }
}

export function SaveButton({ context }: SaveButtonProps) {
  const { savingStatus, autoSaveStatus } = context.state;

  // Category B/C operations take priority (blocking save)
  if (savingStatus) {
    return (
      <div className="x_save " style={{ width: "8em" }}>
        <div
          style={{
            position: "relative",
            marginBottom: "-4px",
            marginTop: "-4px",
            marginLeft: "-4px",
            marginRight: "-4px",
          }}
        >
          <PreloaderGif style={{ width: 44, height: 44 }} />
        </div>
      </div>
    );
  }

  // Auto-save status display
  if (autoSaveStatus === "error") {
    return (
      <div
        className="x_save"
        style={{ width: "8em", cursor: "pointer", color: "#ff4d4f" }}
        onClick={() => void context.handles.flushDirtyChanges()}
        title="Click to retry saving"
      >
        <div>Save error</div>
        <div className="ico ico--20">
          <div>refresh</div>
        </div>
      </div>
    );
  }

  if (autoSaveStatus === "pending") {
    return (
      <div
        className="x_save"
        style={{ width: "8em", opacity: 0.7, cursor: "pointer" }}
        onClick={() => void context.handles.flushDirtyChanges()}
        title="Click to save now"
      >
        <div>Unsaved...</div>
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#faad14",
            marginLeft: 4,
          }}
        />
      </div>
    );
  }

  if (autoSaveStatus === "saving") {
    return (
      <div className="x_save" style={{ width: "8em" }}>
        <div>Saving</div>
        <div
          style={{
            position: "relative",
            marginBottom: "-4px",
            marginTop: "-4px",
            marginLeft: "-4px",
            marginRight: "-4px",
          }}
        >
          <PreloaderGif style={{ width: 44, height: 44 }} />
        </div>
      </div>
    );
  }

  // idle or saved
  return (
    <div className="x_save x_save--saved" style={{ width: "8em" }}>
      <div>Saved</div>
      <div className="ico ico--20">
        <div>check</div>
      </div>
    </div>
  );
}
